import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useNavigate } from "react-router-dom";
import { Heart, ArrowLeft, Plus, Edit, Trash2, Gift as GiftIcon, DollarSign, Upload, Image, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";


export default function GiftManager() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGift, setEditingGift] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    allow_partial: false,
    show_progress: true,
    quantity_total: "1"
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const siteRes = await fetch(`${API}/sites/my`, { credentials: 'include' });
        if (siteRes.ok) {
          const siteData = await siteRes.json();
          if (siteData) {
            setSite(siteData);
            const giftsRes = await fetch(`${API}/sites/${siteData.site_id}/gifts`, { credentials: 'include' });
            if (giftsRes.ok) {
              const giftsData = await giftsRes.json();
              setGifts(giftsData);
            }
          } else {
            navigate('/onboarding');
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error("Apenas arquivos JPG, PNG ou WEBP são permitidos");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Imagem deve ter no máximo 5MB");
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      category: "",
      allow_partial: false,
      show_progress: true,
      quantity_total: "1"
    });
    setEditingGift(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingGift) {
        const payload = {
          name: formData.name,
          description: formData.description || null,
          price: parseFloat(formData.price),
          category: formData.category || null,
          allow_partial: formData.allow_partial,
          show_progress: formData.show_progress,
          quantity_total: parseInt(formData.quantity_total) || 1
        };

        const res = await fetch(`${API}/sites/${site.site_id}/gifts/${editingGift.gift_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          let gift = await res.json();
          
          if (imageFile) {
            const imageFormData = new FormData();
            imageFormData.append('file', imageFile);
            
            const imageRes = await fetch(`${API}/sites/${site.site_id}/gifts/${editingGift.gift_id}/upload-image`, {
              method: 'POST',
              credentials: 'include',
              body: imageFormData
            });
            
            if (imageRes.ok) {
              const imageData = await imageRes.json();
              gift = { ...gift, image_url: imageData.url };
            }
          }
          
          setGifts(prev => prev.map(g => g.gift_id === gift.gift_id ? gift : g));
          toast.success("Presente atualizado!");
        }
      } else {
        const formDataUpload = new FormData();
        formDataUpload.append('name', formData.name);
        formDataUpload.append('price', formData.price);
        if (formData.description) formDataUpload.append('description', formData.description);
        if (formData.category) formDataUpload.append('category', formData.category);
        formDataUpload.append('allow_partial', formData.allow_partial);
        formDataUpload.append('show_progress', formData.show_progress);
        formDataUpload.append('quantity_total', formData.quantity_total || "1");
        if (imageFile) formDataUpload.append('file', imageFile);

        const res = await fetch(`${API}/sites/${site.site_id}/gifts/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formDataUpload
        });

        if (res.ok) {
          const gift = await res.json();
          setGifts(prev => [...prev, gift]);
          toast.success("Presente adicionado!");
        } else {
          throw new Error('Erro ao criar presente');
        }
      }
      
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.message || "Erro ao salvar presente");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (gift) => {
    setEditingGift(gift);
    setFormData({
      name: gift.name,
      description: gift.description || "",
      price: gift.price.toString(),
      category: gift.category || "",
      allow_partial: gift.allow_partial,
      show_progress: gift.show_progress !== false,
      quantity_total: (gift.quantity_total || 1).toString()
    });
    if (gift.image_url) {
      setImagePreview(gift.image_url.startsWith('http') ? gift.image_url : `${process.env.REACT_APP_BACKEND_URL}${gift.image_url}`);
    }
    setDialogOpen(true);
  };

  const handleDelete = async (giftId) => {
    if (!window.confirm("Tem certeza que deseja excluir este presente?")) return;

    try {
      const res = await fetch(`${API}/sites/${site.site_id}/gifts/${giftId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setGifts(prev => prev.filter(g => g.gift_id !== giftId));
        toast.success("Presente excluído!");
      }
    } catch (error) {
      toast.error("Erro ao excluir presente");
    }
  };

  const toggleGiftActive = async (gift) => {
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/gifts/${gift.gift_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !gift.is_active })
      });

      if (res.ok) {
        const updated = await res.json();
        setGifts(prev => prev.map(g => g.gift_id === updated.gift_id ? updated : g));
      }
    } catch (error) {
      toast.error("Erro ao atualizar presente");
    }
  };

  const toggleShowProgress = async (gift) => {
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/gifts/${gift.gift_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ show_progress: !gift.show_progress })
      });

      if (res.ok) {
        const updated = await res.json();
        setGifts(prev => prev.map(g => g.gift_id === updated.gift_id ? updated : g));
        toast.success(updated.show_progress ? "Arrecadação visível" : "Arrecadação oculta");
      }
    } catch (error) {
      toast.error("Erro ao atualizar presente");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#F3C9CF]/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-[#6E0F1A]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <GiftIcon className="w-6 h-6 text-[#B3121B]" />
              <span className="font-serif text-xl text-[#6E0F1A]">Lista de Presentes</span>
            </div>
          </div>
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full" 
            data-testid="add-gift-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Presente
          </Button>
        </div>
      </header>

      {/* Dialog separado */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#6E0F1A]">
              {editingGift ? "Editar Presente" : "Novo Presente"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Image Upload */}
                <div>
                  <Label className="text-[#6E0F1A]">Imagem do Presente</Label>
                  <div 
                    className="mt-2 border-2 border-dashed border-[#F3C9CF] rounded-xl p-4 text-center cursor-pointer hover:border-[#B3121B] transition-colors"
                    onClick={() => document.getElementById('gift-image-input').click()}
                  >
                    {imagePreview ? (
                      <div className="relative">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <p className="text-xs text-[#6E0F1A]/60 mt-2">Clique para alterar</p>
                      </div>
                    ) : (
                      <div className="py-4">
                        <Image className="w-10 h-10 text-[#B3121B] mx-auto mb-2" />
                        <p className="text-sm text-[#6E0F1A]">Clique para adicionar imagem</p>
                        <p className="text-xs text-[#6E0F1A]/50 mt-1">JPG, PNG ou WEBP (máx. 5MB)</p>
                      </div>
                    )}
                  </div>
                  <input
                    id="gift-image-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageChange}
                    className="hidden"
                    data-testid="gift-image-input"
                  />
                </div>

                <div>
                  <Label htmlFor="name" className="text-[#6E0F1A]">Nome do Presente *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-wedding mt-2"
                    data-testid="gift-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="price" className="text-[#6E0F1A]">Valor (R$) *</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    step="0.01"
                    min="1"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    className="input-wedding mt-2"
                    data-testid="gift-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="quantity_total" className="text-[#6E0F1A]">Quantidade Disponível *</Label>
                  <Input
                    id="quantity_total"
                    name="quantity_total"
                    type="number"
                    min="1"
                    value={formData.quantity_total}
                    onChange={handleChange}
                    required
                    className="input-wedding mt-2"
                    data-testid="gift-quantity-input"
                  />
                  <p className="text-xs text-[#6E0F1A]/50 mt-1">
                    Ex: 5 diárias de hotel disponíveis para presentear
                  </p>
                </div>
                <div>
                  <Label htmlFor="description" className="text-[#6E0F1A]">Descrição</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="input-wedding mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="category" className="text-[#6E0F1A]">Categoria</Label>
                  <Input
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    placeholder="Ex: Decoração, Experiências..."
                    className="input-wedding mt-2"
                  />
                </div>
                
                {/* Toggle Options */}
                <div className="space-y-3 pt-2 border-t border-[#F3C9CF]/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-[#6E0F1A]/60" />
                      <Label htmlFor="show_progress" className="text-[#6E0F1A] cursor-pointer">
                        Mostrar arrecadação
                      </Label>
                    </div>
                    <Switch
                      id="show_progress"
                      checked={formData.show_progress}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_progress: checked }))}
                    />
                  </div>
                  <p className="text-xs text-[#6E0F1A]/50 ml-6">
                    Quando ativado, exibe o progresso de arrecadação para os convidados
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#6E0F1A]/60" />
                      <Label htmlFor="allow_partial" className="text-[#6E0F1A] cursor-pointer">
                        Contribuição parcial
                      </Label>
                    </div>
                    <Switch
                      id="allow_partial"
                      checked={formData.allow_partial}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_partial: checked }))}
                    />
                  </div>
                  <p className="text-xs text-[#6E0F1A]/50 ml-6">
                    Permite que convidados contribuam com qualquer valor
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full py-6"
                  data-testid="save-gift-btn"
                >
                  {submitting ? "Salvando..." : editingGift ? "Atualizar" : "Adicionar"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {gifts.length === 0 ? (
          <Card className="card-dashboard text-center py-16">
            <CardContent>
              <div className="w-20 h-20 bg-[#F3C9CF]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <GiftIcon className="w-10 h-10 text-[#B3121B]" />
              </div>
              <h2 className="font-serif text-2xl text-[#6E0F1A] mb-3">
                Nenhum presente ainda
              </h2>
              <p className="text-[#6E0F1A]/70 mb-6">
                Comece adicionando itens à sua lista de presentes
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gifts.map((gift) => {
              const progress = gift.price > 0 ? (gift.amount_received / gift.price) * 100 : 0;
              const imageUrl = gift.image_url ? 
                (gift.image_url.startsWith('http') ? gift.image_url : `${process.env.REACT_APP_BACKEND_URL}${gift.image_url}`) 
                : null;
              const showProgress = gift.show_progress !== false;
              
              return (
                <Card 
                  key={gift.gift_id} 
                  className={`card-dashboard ${!gift.is_active ? 'opacity-60' : ''}`}
                  data-testid={`gift-card-${gift.gift_id}`}
                >
                  <CardContent className="pt-6">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={gift.name}
                        className="w-full h-40 object-cover rounded-lg mb-4"
                      />
                    ) : (
                      <div className="w-full h-40 bg-[#F3C9CF]/20 rounded-lg mb-4 flex items-center justify-center">
                        <GiftIcon className="w-12 h-12 text-[#F3C9CF]" />
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-[#6E0F1A]">{gift.name}</h3>
                        {gift.category && (
                          <span className="text-xs text-[#6E0F1A]/50 uppercase tracking-wide">
                            {gift.category}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleShowProgress(gift)}
                          className={`${showProgress ? 'text-[#567D50]' : 'text-[#6E0F1A]/40'} hover:text-[#B3121B]`}
                          title={showProgress ? "Ocultar arrecadação" : "Mostrar arrecadação"}
                        >
                          {showProgress ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(gift)}
                          className="text-[#6E0F1A]/60 hover:text-[#B3121B]"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(gift.gift_id)}
                          className="text-[#6E0F1A]/60 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {gift.description && (
                      <p className="text-sm text-[#6E0F1A]/70 mb-3 line-clamp-2">
                        {gift.description}
                      </p>
                    )}
                    
                    {/* Progress Section - Always visible in admin */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[#6E0F1A]/60 flex items-center gap-1">
                          Arrecadado
                          {!showProgress && (
                            <span className="text-xs bg-[#F3C9CF]/50 text-[#6E0F1A]/60 px-1.5 py-0.5 rounded">
                              oculto
                            </span>
                          )}
                        </span>
                        <span className="text-[#567D50] font-medium">
                          {formatCurrency(gift.amount_received)} / {formatCurrency(gift.price)}
                        </span>
                      </div>
                      <Progress value={Math.min(progress, 100)} className="h-2" />
                    </div>

                    {/* Quantity Section */}
                    <div className="mb-3 p-2 bg-[#F3C9CF]/10 rounded-lg">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6E0F1A]/60">Quantidade</span>
                        <span className="text-[#6E0F1A] font-medium">
                          {gift.quantity_purchased || 0} / {gift.quantity_total || 1} presenteados
                        </span>
                      </div>
                      {(gift.quantity_purchased || 0) >= (gift.quantity_total || 1) && (
                        <div className="text-xs text-center mt-1 text-[#B3121B] font-medium">
                          Esgotado
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-[#F3C9CF]/30">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={gift.is_active}
                          onCheckedChange={() => toggleGiftActive(gift)}
                        />
                        <span className="text-xs text-[#6E0F1A]/60">
                          {gift.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      {gift.is_fulfilled && (
                        <span className="text-xs bg-[#567D50] text-white px-2 py-1 rounded-full">
                          Completo!
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
